import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Box, Button } from '@material-ui/core'

import { Container, ContentHeader, LoadingFeedback, Page } from 'components'
import { DocumentContent, DocumentTabs } from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'
import useDaiChat from 'hooks/useDaiChat'

import { routes } from 'Routes'
import theme from 'theme'
import schema from './components/DocumentEditForm/schema'
import { SETTINGS_FIELDS } from './components/DocumentEditForm/settingsSchema'
import { EDITOR_FIELDS } from './components/DocumentEditForm/editorSchema'
import * as service from 'service'
import constants from 'constants/index'

const {
  ACCEPTED_DOCUMENTS_CATEGORY_ID,
  ATTACHMENT_CONTENT_KIND,
  DEPARTMENT_ACCESS_PERMISSION_ID,
  DOCUMENTS_CATEGORY_ID,
  SETTINGS_DOCUMENTS_CATEGORY_ID,
  USER_ACCESS_PERMISSION_ID,
  OWNER_ACCESS_PERMISSION_ID,
  WRITTEN_CONTENT_KIND,
  APPROVAL_DOCUMENTS_CATEGORY_ID,
} = constants.documents

const DocumentsNew = () => {
  const [currentTab, setCurrentTab] = useState(DOCUMENTS_CATEGORY_ID)
  const [loading, setLoading] = useState(false)
  const { handleNewChat } = useDaiChat()

  const settingsErrorRef = useRef(null)

  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()
  const { user } = useAuth()

  const queryParams = new URLSearchParams(location.search)
  const templateId = Number(queryParams.get('templateId'))

  const { response: templateResponse, isLoading: templateIsLoading } = useFetch(
    service.dponet.documentTemplates.get,
    { documentTemplateId: templateId },
    [templateId],
    !!templateId,
  )

  const { response: usersResponse, loading: usersIsLoading } = useFetch(
    service.dponet.departments.availableUsers,
    {},
  )

  const { response: departmentsResponse, loading: departmentsIsLoading } =
    useFetch(
      service.dponet.departments.getAll,
      { perPage: 1000, active: true, order: 'departments.name ASC' },
      [],
    )

  // const documentTemplate = templateResponse?.data?.documentTemplate
  const users = usersResponse?.data?.users || []
  const availableUsers = users.filter((u) => u.id !== user.id)
  const departments = departmentsResponse?.data?.departments || []

  const defaultValues = {
    allowAutomaticEditing: false,
    attachment: null,
    category: '',
    checkOldVersions: false,
    code: '',
    collectAccepted: false,
    collectApproval: false,
    collectSignedDocument: false,
    content: '',
    contentKind: WRITTEN_CONTENT_KIND,
    dueDate: false,
    dueDateValue: null,
    expirationType: '0',
    expirationTime: null,
    expirationUnit: null,
    otherCategory: '',
    publicDocument: false,
    selectedDepartments: [],
    selectedUsers: [],
    signedDocument: null,
    title: '',
    visibilityType: OWNER_ACCESS_PERMISSION_ID,
  }

  const formMethods = useForm({ validationSchema: schema(), defaultValues })
  const { handleSubmit, getValues, triggerValidation, setValue, watch } =
    formMethods
  // const category = watch('category')
  // const otherCategory = watch('otherCategory')

  const contentKind = watch('contentKind')
  const isAttachmentContent = contentKind === ATTACHMENT_CONTENT_KIND

  // Possíveis implementações futuras

  // useEffect(() => {
  //   if (documentTemplate) {
  //     setValue('title', documentTemplate.name || '')
  //     setValue('content', documentTemplate.content || '')
  //     setValue('category', 'other')

  //     handleNewChat({
  //       agent: constants.dai.agents.DOCUMENT_AGENT,
  //       context: {
  //         documentTemplateId: documentTemplate.id,
  //       },
  //       initialMessage:
  //         'Iniciar a criação do documento com base no template selecionado',
  //     })
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [documentTemplate])

  // useEffect(() => {
  //   if (documentTemplate && category === 'other' && !otherCategory) {
  //     setValue('otherCategory', documentTemplate.documentCategory?.name || '')
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [documentTemplate, category])

  useEffect(() => {
    if (isAttachmentContent) {
      setValue('publicDocument', false)
      setValue('collectAccepted', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAttachmentContent])

  const handleBackRedirection = () => history.push(routes.documents.all)

  const handleSubmitValidation = async (status) => {
    const isValid = await triggerValidation()

    if (!isValid) {
      const hasSettingsError = SETTINGS_FIELDS.some(
        (field) => !!formMethods.errors[field],
      )
      const hasEditorError = EDITOR_FIELDS.some(
        (field) => !!formMethods.errors[field],
      )
      if (hasSettingsError && !hasEditorError) {
        setCurrentTab(SETTINGS_DOCUMENTS_CATEGORY_ID)
        setTimeout(
          () =>
            settingsErrorRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            }),
          1000,
        )
      }
      return
    }

    handleSubmit((data) => onSubmit(data, status))()
  }

  const onSubmit = async (data, status) => {
    const formValues = getValues()
    const currentVisibilityType = formValues.visibilityType

    let targetIds = []
    if (currentVisibilityType === USER_ACCESS_PERMISSION_ID) {
      targetIds = formValues.selectedUsers?.map((u) => u.id) || []
    } else if (currentVisibilityType === DEPARTMENT_ACCESS_PERMISSION_ID) {
      targetIds = formValues.selectedDepartments?.map((d) => d.id) || []
    }

    try {
      setLoading(true)

      const changedValues = helpers.form.getChangedValues(data, defaultValues)
      const {
        selectedUsers,
        selectedDepartments,
        visibilityType,
        dueDateValue,
        dueDate,
        expirationType,
        expirationUnit,
        expirationTime,
        ...documentData
      } = changedValues

      const expirationData = {
        active: data?.dueDate || false,
        expirationType: data?.expirationType || null,
        expirationUnit: data?.expirationUnit || null,
        expirationTime:
          data?.expirationTime !== undefined && data?.expirationTime !== ''
            ? data.expirationTime
            : null,
        dueDate: helpers.formatters.date(data?.dueDateValue) || null,
      }

      const response = await service.dponet.privacyPolicies.create({
        status,
        ...documentData,
        visibilityType: currentVisibilityType,
        updateExpiration: true,
        ...expirationData,
      })

      const newPrivacyPolicyId = response?.data?.privacyPolicy?.id

      if (
        newPrivacyPolicyId &&
        (currentVisibilityType === USER_ACCESS_PERMISSION_ID ||
          currentVisibilityType === DEPARTMENT_ACCESS_PERMISSION_ID)
      ) {
        await service.dponet.privacyPolicies.updatePrivacyPermissions({
          privacyPolicyId: newPrivacyPolicyId,
          visibilityType: currentVisibilityType,
          targetIds,
        })
      }

      snackbar.open({
        message: 'Documento criado com sucesso!',
        variant: 'success',
      })

      const privacyPolicy = response?.data?.privacyPolicy
      const shouldRedirectToDocumentsList =
        !privacyPolicy?.collectAccepted && !privacyPolicy?.collectApproval
      shouldRedirectToDocumentsList
        ? handleBackRedirection()
        : history.push({
            pathname: reverse(routes.documents.edit, {
              documentId: privacyPolicy?.id,
            }),
            search: `?tab=${
              privacyPolicy?.collectApproval
                ? APPROVAL_DOCUMENTS_CATEGORY_ID
                : ACCEPTED_DOCUMENTS_CATEGORY_ID
            }`,
          })
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu uma falha ao tentar criar o documento!',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const formActionButtons = (
    <Box display="flex" gridGap={theme.spacing(1)} justifyContent="flex-end">
      <Button
        variant="outlined"
        color="primary"
        onClick={
          currentTab === DOCUMENTS_CATEGORY_ID
            ? handleBackRedirection
            : () => setCurrentTab(DOCUMENTS_CATEGORY_ID)
        }
      >
        Voltar
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() =>
          handleSubmitValidation(constants.documents.DRAFT_STATUS_ID)
        }
      >
        Salvar
      </Button>
    </Box>
  )

  return (
    <Page title="Novo documento">
      <Container maxWidth={false}>
        <LoadingFeedback open={loading || templateIsLoading} />
        <ContentHeader title="Novo documento" />
        <DocumentTabs
          currentTab={currentTab}
          onChange={(_, tab) => setCurrentTab(tab)}
          showSettingsTab
        />
        <DocumentContent
          type="create"
          currentTab={currentTab}
          pageActions={{ handleBackRedirection, setLoading }}
          form={{
            methods: formMethods,
            actionButtons: formActionButtons,
            errorRef: settingsErrorRef,
          }}
          settings={{
            availableUsers,
            departments,
            usersIsLoading,
            departmentsIsLoading,
          }}
        />
      </Container>
    </Page>
  )
}

export default DocumentsNew
